"use client"

import React from 'react'
import { cn } from './utils'
import { UIButton } from '../composed/buttons/ui-button'
import { UITypography } from '../composed/display/ui-typography'
import { UIBox } from '../composed/layout/ui-box'
import { Copy, ExternalLink } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export type MarkdownVariant = 'default' | 'compact' | 'prose' | 'snippet'

// ============================================================================
// Variant-aware style configurations
// ============================================================================

const headingStyles = {
  default: {
    h1: { size: '2xl' as const, weight: 'bold' as const, className: 'mt-8 mb-6 first:mt-0' },
    h2: { size: 'xl' as const, weight: 'semibold' as const, className: 'mt-8 mb-6 first:mt-0' },
    h3: { size: 'lg' as const, weight: 'semibold' as const, className: 'mt-6 mb-4 first:mt-0' },
    h4: { size: 'default' as const, weight: 'semibold' as const, className: 'mt-4 mb-2 first:mt-0' },
    h5: { size: 'sm' as const, weight: 'semibold' as const, className: 'mt-3 mb-1.5 first:mt-0' },
    h6: { size: 'sm' as const, weight: 'medium' as const, className: 'mt-2 mb-1 first:mt-0' },
  },
  compact: {
    h1: { size: 'lg' as const, weight: 'semibold' as const, className: 'mt-4 mb-2 first:mt-0' },
    h2: { size: 'default' as const, weight: 'semibold' as const, className: 'mt-3 mb-1.5 first:mt-0' },
    h3: { size: 'sm' as const, weight: 'semibold' as const, className: 'mt-2 mb-1 first:mt-0' },
    h4: { size: 'sm' as const, weight: 'medium' as const, className: 'mt-1.5 mb-0.5 first:mt-0' },
    h5: { size: 'xs' as const, weight: 'medium' as const, className: 'mt-1 mb-0.5 first:mt-0' },
    h6: { size: 'xs' as const, weight: 'normal' as const, className: 'mt-0.5 mb-0.5 first:mt-0' },
  },
  prose: {
    h1: { size: '2xl' as const, weight: 'bold' as const, className: 'mt-8 mb-6 first:mt-0' },
    h2: { size: 'xl' as const, weight: 'semibold' as const, className: 'mt-8 mb-6 first:mt-0' },
    h3: { size: 'lg' as const, weight: 'semibold' as const, className: 'mt-6 mb-4 first:mt-0' },
    h4: { size: 'default' as const, weight: 'semibold' as const, className: 'mt-4 mb-2 first:mt-0' },
    h5: { size: 'sm' as const, weight: 'semibold' as const, className: 'mt-3 mb-1.5 first:mt-0' },
    h6: { size: 'sm' as const, weight: 'medium' as const, className: 'mt-2 mb-1 first:mt-0' },
  },
  snippet: {
    h1: { size: 'sm' as const, weight: 'semibold' as const, className: 'mt-1.5 mb-0.5 first:mt-0' },
    h2: { size: 'sm' as const, weight: 'medium' as const, className: 'mt-1 mb-0.5 first:mt-0' },
    h3: { size: 'xs' as const, weight: 'medium' as const, className: 'mt-0.5 mb-0.5 first:mt-0' },
    h4: { size: 'xs' as const, weight: 'medium' as const, className: 'mt-0.5 mb-0.5 first:mt-0' },
    h5: { size: 'xs' as const, weight: 'normal' as const, className: 'mt-0.5 mb-0 first:mt-0' },
    h6: { size: 'xs' as const, weight: 'normal' as const, className: 'mt-0.5 mb-0 first:mt-0' },
  },
}

const spacingStyles = {
  default: {
    paragraph: 'my-4 leading-relaxed first:mt-0 last:mb-0',
    codeBlock: 'my-4',
    codeBlockPadding: 'p-4',
    list: 'my-4 ml-6',
    listItem: 'mt-2',
    blockquote: 'my-4 border-l-2 border-primary pl-6',
    table: 'my-8',
  },
  compact: {
    paragraph: 'my-2 leading-relaxed first:mt-0 last:mb-0',
    codeBlock: 'my-2',
    codeBlockPadding: 'p-3',
    list: 'my-2 ml-4',
    listItem: 'mt-1',
    blockquote: 'my-2 border-l-2 border-primary pl-4',
    table: 'my-4',
  },
  prose: {
    paragraph: 'my-4 leading-relaxed first:mt-0 last:mb-0',
    codeBlock: 'my-4',
    codeBlockPadding: 'p-4',
    list: 'my-4 ml-6',
    listItem: 'mt-2',
    blockquote: 'my-4 border-l-2 border-primary pl-6',
    table: 'my-8',
  },
  snippet: {
    paragraph: 'my-1 leading-normal first:mt-0 last:mb-0',
    codeBlock: 'my-1.5',
    codeBlockPadding: 'p-2',
    list: 'my-1 ml-3',
    listItem: 'mt-0.5',
    blockquote: 'my-1.5 border-l border-primary pl-3',
    table: 'my-2',
  },
}

// ============================================================================
// Component Factory
// ============================================================================

/**
 * 공유 마크다운 컴포넌트 설정
 * UIMessage, UIAIResponse, UIMarkdownContent에서 공통으로 사용
 *
 * @param locale - 로케일 ('ko' | 'en')
 * @param variant - 스타일 변형
 *   - 'default': 기본 문서 스타일 (큰 헤딩, 넓은 마진)
 *   - 'compact': 압축된 스타일 (중간 헤딩, 좁은 마진)
 *   - 'prose': 산문체 문서 스타일 (default와 동일)
 *   - 'snippet': 검색 결과용 스타일 (작은 헤딩, 최소 마진)
 */
export const createMarkdownComponents = (
  locale: 'ko' | 'en' = 'ko',
  variant: MarkdownVariant = 'default'
) => {
  const heading = headingStyles[variant]
  const spacing = spacingStyles[variant]

  // snippet variant에서는 variant="inline"을 사용하여 반응형 text-size 클래스(lg:text-5xl 등) 제거
  // 다른 variant에서는 기본 heading variant 사용
  const isSnippet = variant === 'snippet'

  return {
    // 헤딩 요소들 - snippet에서는 inline variant로 반응형 크기 override 방지
    h1: ({ children, ...props }: any) => (
      <UITypography
        as="h1"
        variant={isSnippet ? 'inline' : 'h1'}
        size={heading.h1.size}
        weight={heading.h1.weight}
        className={cn(heading.h1.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),
    h2: ({ children, ...props }: any) => (
      <UITypography
        as="h2"
        variant={isSnippet ? 'inline' : 'h2'}
        size={heading.h2.size}
        weight={heading.h2.weight}
        className={cn(heading.h2.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),
    h3: ({ children, ...props }: any) => (
      <UITypography
        as="h3"
        variant={isSnippet ? 'inline' : 'h3'}
        size={heading.h3.size}
        weight={heading.h3.weight}
        className={cn(heading.h3.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),
    h4: ({ children, ...props }: any) => (
      <UITypography
        as="h4"
        variant={isSnippet ? 'inline' : 'h4'}
        size={heading.h4.size}
        weight={heading.h4.weight}
        className={cn(heading.h4.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),
    h5: ({ children, ...props }: any) => (
      <UITypography
        as="h5"
        variant={isSnippet ? 'inline' : 'h5'}
        size={heading.h5.size}
        weight={heading.h5.weight}
        className={cn(heading.h5.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),
    h6: ({ children, ...props }: any) => (
      <UITypography
        as="h6"
        variant={isSnippet ? 'inline' : 'h6'}
        size={heading.h6.size}
        weight={heading.h6.weight}
        className={cn(heading.h6.className, isSnippet && 'block')}
        {...props}
      >
        {children}
      </UITypography>
    ),

    // 문단 요소 - className으로 색상 상속 강제 설정
    p: ({ children, ...props }: any) => (
      <UITypography variant="p" className={cn(spacing.paragraph, '!text-inherit')} {...props}>
        {children}
      </UITypography>
    ),

    // 코드 블록
    pre: ({ children, ...props }: any) => {
      const extractText = (node: any): string => {
        if (typeof node === 'string') return node
        if (typeof node === 'number') return node.toString()
        if (!node) return ''
        if (Array.isArray(node)) return node.map(extractText).join('')
        if (typeof node === 'object' && node.props) {
          return extractText(node.props.children)
        }
        return ''
      }

      const codeText: string = extractText(children)
      const firstLine = codeText.split('\n')[0]?.slice(0, 30) || ''
      const uniqueLabel = locale === 'ko'
        ? `코드 블록${firstLine ? `: ${firstLine}` : ''}${firstLine.length >= 30 ? '...' : ''}`
        : `Code block${firstLine ? `: ${firstLine}` : ''}${firstLine.length >= 30 ? '...' : ''}`

      return (
        <UIBox className={cn('relative', spacing.codeBlock)}>
          <pre
            className={cn(
              'bg-muted rounded-lg overflow-x-auto text-sm',
              spacing.codeBlockPadding,
              'border border-border focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
            )}
            tabIndex={0}
            role="region"
            aria-label={uniqueLabel}
            {...props}
          >
            {children}
          </pre>
          {variant !== 'snippet' && (
            <UIButton
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-6 w-6 p-0"
              onClick={() => {
                const codeText = extractText(children)
                navigator.clipboard.writeText(codeText)
              }}
              aria-label={locale === 'ko' ? '코드 복사' : 'Copy code'}
            >
              <Copy className="w-3 h-3" />
              <UITypography as="span" variant="small" className="sr-only">
                {locale === 'ko' ? '코드 복사' : 'Copy code'}
              </UITypography>
            </UIButton>
          )}
        </UIBox>
      )
    },

    // 인라인 코드
    code: ({ children, className, ...props }: any) => {
      // pre 안에 있는 code는 배경 투명, 인라인 code는 배경 적용
      const isInPre = className?.includes('language-') || props.hasOwnProperty('data-highlighted')

      return (
        <code
          className={cn(
            'relative font-mono text-sm',
            // pre 안의 code는 배경 투명, 인라인 code는 bg-muted
            isInPre
              ? 'bg-transparent'
              : 'rounded bg-muted px-[0.3rem] py-[0.2rem]',
            className
          )}
          {...props}
        >
          {children}
        </code>
      )
    },

    // 링크
    a: ({ href, children, ...props }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-4 hover:no-underline"
        {...props}
      >
        {children}
        <ExternalLink className={cn('inline ml-1', variant === 'snippet' ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
      </a>
    ),

    // 리스트
    ul: ({ children, ...props }: any) => (
      <ul className={cn(spacing.list, 'list-disc')} {...props}>
        {children}
      </ul>
    ),

    ol: ({ children, ...props }: any) => (
      <ol className={cn(spacing.list, 'list-decimal')} {...props}>
        {children}
      </ol>
    ),

    li: ({ children, ...props }: any) => (
      <li className={spacing.listItem} {...props}>
        {children}
      </li>
    ),

    // 인용구
    blockquote: ({ children, ...props }: any) => (
      <blockquote className={cn(spacing.blockquote, 'italic')} {...props}>
        {children}
      </blockquote>
    ),

    // 테이블
    table: ({ children, ...props }: any) => (
      <UIBox
        className={cn(
          spacing.table,
          'w-full overflow-y-auto focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded'
        )}
        tabIndex={0}
        role="region"
        aria-label={locale === 'ko' ? '데이터 테이블' : 'Data table'}
      >
        <table className="w-full border-collapse border border-border" {...props}>
          {children}
        </table>
      </UIBox>
    ),

    th: ({ children, ...props }: any) => (
      <th
        className={cn(
          'border border-border text-left font-bold bg-muted',
          variant === 'snippet' ? 'px-2 py-1 text-xs' : 'px-4 py-2'
        )}
        {...props}
      >
        {children}
      </th>
    ),

    td: ({ children, ...props }: any) => (
      <td
        className={cn(
          'border border-border',
          variant === 'snippet' ? 'px-2 py-1 text-xs' : 'px-4 py-2'
        )}
        {...props}
      >
        {children}
      </td>
    ),

    // 이미지 - VSync 출력 지원 (src 없는 경우 대체 텍스트 표시)
    img: ({ src, alt, ...props }: any) => {
      const imageType = props['image_type'] as string | undefined

      // src가 있으면 실제 이미지 렌더링
      if (src) {
        return (
          <img
            src={src}
            alt={alt || ''}
            className={cn(
              'max-w-full h-auto rounded',
              variant === 'snippet' ? 'my-1' : 'my-2'
            )}
            loading="lazy"
          />
        )
      }

      // src 없으면 설명 텍스트로 대체 (VSync 출력)
      return (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 bg-muted/50 rounded text-muted-foreground border border-border/50',
            variant === 'snippet' ? 'px-1.5 py-0.5 text-[10px] my-0.5' : 'px-2 py-1 text-xs my-1'
          )}
        >
          <span className="font-medium">[{imageType || 'image'}]</span>
          <span className="text-foreground/80">{alt || 'Image'}</span>
        </span>
      )
    },
  }
}
